import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { isAdmin } from '../../utils/permissions';
import useTranslation from '../../hooks/useTranslation';
import { projectService } from '../../services/projectService';
import Icon from '../../components/AppIcon';
import { addMonths, addWeeks, addQuarters, addDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfQuarter, endOfQuarter, differenceInDays, format, parseISO, isValid, isBefore, isAfter } from 'date-fns';
import { formatCurrency as fmtCAD } from '../../utils/formatCurrency';
import { statusVariant } from '../../utils/statusVariant';
import Badge from '../../components/ui/Badge';

// ─── Deterministic PM color palette (softer, design-system-aligned) ──────────
const PM_PALETTE = [
  { bg: '#93C5FD', text: '#1E3A5F' },
  { bg: '#C4B5FD', text: '#3B1F6E' },
  { bg: '#6EE7B7', text: '#065F46' },
  { bg: '#FCD34D', text: '#78350F' },
  { bg: '#FCA5A5', text: '#7F1D1D' },
  { bg: '#67E8F9', text: '#164E63' },
  { bg: '#F9A8D4', text: '#831843' },
  { bg: '#BEF264', text: '#365314' },
  { bg: '#FDBA74', text: '#7C2D12' },
  { bg: '#A5B4FC', text: '#312E81' },
];

function hashPmId(pmUserId) {
  if (!pmUserId) return 0;
  let hash = 0;
  for (let i = 0; i < pmUserId?.length; i++) {
    hash = (hash * 31 + pmUserId?.charCodeAt(i)) >>> 0;
  }
  return hash % PM_PALETTE?.length;
}

function getPmColor(pmUserId) {
  return PM_PALETTE?.[hashPmId(pmUserId)];
}

// ─── Date helpers ─────────────────────────────────────────────────────────────
function getWindowBounds(anchor, granularity) {
  const today = anchor;
  if (granularity === 'day') {
    // 7 days before today, 21 days after → 28-day window centered on today
    const start = addDays(today, -7);
    const end = addDays(today, 21);
    return { start, end };
  }
  if (granularity === 'week') {
    const start = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), -2);
    const end = addWeeks(endOfWeek(today, { weekStartsOn: 1 }), 9);
    return { start, end };
  }
  if (granularity === 'quarter') {
    const start = addQuarters(startOfQuarter(today), -1);
    const end = addQuarters(endOfQuarter(today), 4);
    return { start, end };
  }
  // month (default)
  const start = addMonths(startOfMonth(today), -2);
  const end = addMonths(endOfMonth(today), 3);
  return { start, end };
}

// Weekday letter (M T W T F S S) — ISO weekday 1=Mon
const WEEKDAY_LETTERS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

function buildColumns(windowStart, windowEnd, granularity) {
  const cols = [];
  if (granularity === 'day') {
    let cur = windowStart;
    while (!isAfter(cur, windowEnd)) {
      const isoDay = parseInt(format(cur, 'i'), 10); // 1=Mon … 7=Sun
      const weekdayLetter = WEEKDAY_LETTERS?.[isoDay - 1];
      const isWeekend = isoDay >= 6;
      cols?.push({
        label: format(cur, 'd MMM'),       // e.g. "12 Jun"
        weekday: weekdayLetter,
        isWeekend,
        start: cur,
        end: cur,
      });
      cur = addDays(cur, 1);
    }
    return cols;
  }
  if (granularity === 'week') {
    let cur = startOfWeek(windowStart, { weekStartsOn: 1 });
    while (!isAfter(cur, windowEnd)) {
      cols?.push({ label: `W${format(cur, 'w')} ${format(cur, 'MMM d')}`, start: cur, end: endOfWeek(cur, { weekStartsOn: 1 }) });
      cur = addWeeks(cur, 1);
    }
  } else if (granularity === 'quarter') {
    let cur = startOfQuarter(windowStart);
    while (!isAfter(cur, windowEnd)) {
      cols?.push({ label: `Q${format(cur, 'Q yyyy')}`, start: cur, end: endOfQuarter(cur) });
      cur = addQuarters(cur, 1);
    }
  } else {
    let cur = startOfMonth(windowStart);
    while (!isAfter(cur, windowEnd)) {
      cols?.push({ label: format(cur, 'MMM yyyy'), start: cur, end: endOfMonth(cur) });
      cur = addMonths(cur, 1);
    }
  }
  return cols;
}

// ─── Tooltip ──────────────────────────────────────────────────────────────────
const Tooltip = ({ project, x, y, t, visible }) => {
  if (!visible || !project) return null;
  const start = project?.startDate ? parseISO(project?.startDate) : null;
  const end = project?.endDate ? parseISO(project?.endDate) : null;
  const duration = start && end && isValid(start) && isValid(end)
    ? differenceInDays(end, start) + 1
    : null;
  const fmtDate = (d) => d && isValid(d) ? format(d, 'MMM d, yyyy') : '—';
  const fmtCurrency = (v) => v != null ? fmtCAD(v, 0) : '—';

  return (
    <div
      className="fixed z-[9999] pointer-events-none"
      style={{ left: x + 14, top: y - 10 }}
    >
      <div className="bg-[#162637] border border-[#2A3F58] rounded-xl shadow-2xl p-3 min-w-[220px] max-w-[280px]">
        <p className="text-primary font-semibold text-sm leading-tight">{project?.projectNumber}</p>
        <p className="text-[#E6EDF3] text-sm font-medium mt-0.5 leading-tight">{project?.name}</p>
        <div className="mt-2 space-y-1">
          <div className="flex items-center gap-1.5 text-xs text-[#9FB3C8]">
            <Icon name="User" size={12} className="text-[#9FB3C8] flex-shrink-0" />
            <span>{project?.pmFullName || '—'}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-[#9FB3C8]">
            <Icon name="Calendar" size={12} className="text-[#9FB3C8] flex-shrink-0" />
            <span>{fmtDate(start)} → {fmtDate(end)}</span>
          </div>
          {duration && (
            <div className="flex items-center gap-1.5 text-xs text-[#9FB3C8]">
              <Icon name="Clock" size={12} className="text-[#9FB3C8] flex-shrink-0" />
              <span>{duration} {t('projectSchedule.tooltip.days')}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-[#9FB3C8]">
            <Icon name="DollarSign" size={12} className="text-[#9FB3C8] flex-shrink-0" />
            <span>{fmtCurrency(project?.contractValue)}</span>
          </div>
          <div className="mt-1.5">
            <Badge variant={statusVariant('project', project?.status)}>
              {t(`projectStatus.${project?.status}`)}
            </Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const ProjectSchedulePage = () => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { t } = useTranslation();

  // Admin guard
  useEffect(() => {
    if (userProfile !== undefined && !isAdmin(userProfile)) {
      navigate('/project-detail', { replace: true });
    }
  }, [userProfile, navigate]);

  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState('month');
  const [anchor, setAnchor] = useState(new Date());
  const [selectedPMs, setSelectedPMs] = useState([]);
  const [pmFilterOpen, setPmFilterOpen] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, project: null, x: 0, y: 0 });
  const pmFilterRef = useRef(null);

  useEffect(() => {
    const fetchProjects = async () => {
      setLoading(true);
      try {
        const data = await projectService?.getScheduleProjects();
        setProjects(data || []);
      } catch (e) {
        console.error('Error loading schedule projects:', e);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  // Close PM filter dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (pmFilterRef?.current && !pmFilterRef?.current?.contains(e?.target)) {
        setPmFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Unique PMs from projects
  const allPMs = React.useMemo(() => {
    const map = {};
    projects?.forEach(p => {
      if (p?.pmUserId && !map?.[p?.pmUserId]) {
        map[p.pmUserId] = { id: p?.pmUserId, name: p?.pmFullName || p?.pmUserId };
      }
    });
    return Object.values(map)?.sort((a, b) => (a?.name || '')?.localeCompare(b?.name || ''));
  }, [projects]);

  // Filtered projects
  const filteredProjects = React.useMemo(() => {
    if (selectedPMs?.length === 0) return projects;
    return projects?.filter(p => selectedPMs?.includes(p?.pmUserId));
  }, [projects, selectedPMs]);

  // Window bounds & columns
  const { start: windowStart, end: windowEnd } = React.useMemo(
    () => getWindowBounds(anchor, granularity),
    [anchor, granularity]
  );
  const columns = React.useMemo(
    () => buildColumns(windowStart, windowEnd, granularity),
    [windowStart, windowEnd, granularity]
  );

  const totalDays = differenceInDays(windowEnd, windowStart) + 1;

  // Navigate window
  const navigate_window = useCallback((dir) => {
    setAnchor(prev => {
      if (granularity === 'day') return addDays(prev, dir * 14);
      if (granularity === 'week') return addWeeks(prev, dir * 4);
      if (granularity === 'quarter') return addQuarters(prev, dir * 2);
      return addMonths(prev, dir * 3);
    });
  }, [granularity]);

  // Today marker position
  const today = new Date();
  const todayOffset = differenceInDays(today, windowStart);
  const todayPct = (todayOffset / totalDays) * 100;
  const showTodayMarker = todayPct >= 0 && todayPct <= 100;

  // Bar position for a project
  const getBarStyle = (project) => {
    const start = project?.startDate ? parseISO(project?.startDate) : null;
    const end = project?.endDate ? parseISO(project?.endDate) : null;
    if (!start || !end || !isValid(start) || !isValid(end)) return null;

    const clampedStart = isBefore(start, windowStart) ? windowStart : start;
    const clampedEnd = isAfter(end, windowEnd) ? windowEnd : end;
    if (isAfter(clampedStart, windowEnd) || isBefore(clampedEnd, windowStart)) return null;

    const leftDays = differenceInDays(clampedStart, windowStart);
    const widthDays = differenceInDays(clampedEnd, clampedStart) + 1;
    const leftPct = (leftDays / totalDays) * 100;
    const widthPct = (widthDays / totalDays) * 100;
    return { left: `${leftPct}%`, width: `${Math.max(widthPct, 0.5)}%` };
  };

  const togglePM = (pmId) => {
    setSelectedPMs(prev =>
      prev?.includes(pmId) ? prev?.filter(id => id !== pmId) : [...prev, pmId]
    );
  };

  const granularityOptions = [
    { key: 'day', label: t('projectSchedule.granularity.day') },
    { key: 'week', label: t('projectSchedule.granularity.week') },
    { key: 'month', label: t('projectSchedule.granularity.month') },
    { key: 'quarter', label: t('projectSchedule.granularity.quarter') },
  ];

  if (!isAdmin(userProfile) && userProfile !== undefined) return null;

  // Day-view: each column has a fixed pixel width for readability
  const DAY_COL_WIDTH = 38; // px per day column

  return (
    <div className="min-h-screen bg-background">
      {/* ── Page Header ── */}
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-1">
          <Icon name="Calendar" size={20} className="text-primary" />
          <h1 className="text-lg font-semibold text-[#2C3E50]">{t('projectSchedule.title')}</h1>
        </div>
        <div className="flex items-center justify-between">
          <p className="text-xs font-normal text-[#6B7280]">{t('projectSchedule.subtitle')}</p>
          <div className="flex items-center gap-2" />
        </div>
      </div>
      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        {/* LEFT: Granularity pills */}
        <div className="flex items-center h-8 bg-white rounded-lg border border-gray-200 px-1 shadow-sm flex-shrink-0">
          {granularityOptions?.map(opt => (
            <button
              key={opt?.key}
              onClick={() => { setGranularity(opt?.key); setAnchor(new Date()); }}
              className={`h-6 px-3 text-xs font-medium rounded-md transition-all duration-200 ${
                granularity === opt?.key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {opt?.label}
            </button>
          ))}
        </div>

        {/* MIDDLE: PM Color Legend (relocated from bottom card) */}
        {!loading && allPMs?.length > 0 && (
          <div className="flex items-center gap-3 flex-wrap">
            {allPMs?.map(pm => {
              const color = getPmColor(pm?.id);
              return (
                <div key={pm?.id} className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color?.bg }} />
                  <span className="text-xs text-[#374151]">{pm?.name}</span>
                </div>
              );
            })}
          </div>
        )}

        {/* RIGHT: PM filter + navigation */}
        <div className="flex items-center gap-2">
          {/* PM Filter */}
          <div className="relative" ref={pmFilterRef}>
            <button
              onClick={() => setPmFilterOpen(v => !v)}
              className="flex items-center gap-2 px-3 py-1.5 h-8 rounded-lg border border-gray-200 bg-white text-xs text-[#374151] hover:border-primary transition-colors shadow-sm"
            >
              <Icon name="Users" size={14} className="text-[#9CA3AF]" />
              <span>
                {selectedPMs?.length === 0
                  ? t('projectSchedule.filter.allPMs')
                  : `${selectedPMs?.length} PM${selectedPMs?.length > 1 ? 's' : ''}`}
              </span>
              <Icon name="ChevronDown" size={13} className="text-[#9CA3AF]" />
            </button>
            {pmFilterOpen && (
              <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[180px] py-1">
                <button
                  onClick={() => setSelectedPMs([])}
                  className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 ${selectedPMs?.length === 0 ? 'text-primary font-medium' : 'text-[#374151]'}`}
                >
                  {t('projectSchedule.filter.allPMs')}
                </button>
                {allPMs?.map(pm => {
                  const color = getPmColor(pm?.id);
                  const checked = selectedPMs?.includes(pm?.id);
                  return (
                    <button
                      key={pm?.id}
                      onClick={() => togglePM(pm?.id)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-[#374151] hover:bg-gray-50"
                    >
                      <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color?.bg }} />
                      <span className="flex-1 text-left truncate">{pm?.name}</span>
                      {checked && <Icon name="Check" size={13} className="text-primary" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Navigation */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => navigate_window(-1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-[#6B7280] hover:border-primary hover:text-primary transition-colors shadow-sm"
            >
              <Icon name="ChevronLeft" size={16} />
            </button>
            <button
              onClick={() => setAnchor(new Date())}
              className="px-3 h-8 rounded-lg border border-gray-200 bg-white text-xs text-[#374151] hover:border-primary hover:text-primary transition-colors font-medium shadow-sm"
            >
              {t('projectSchedule.nav.today')}
            </button>
            <button
              onClick={() => navigate_window(1)}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-[#6B7280] hover:border-primary hover:text-primary transition-colors shadow-sm"
            >
              <Icon name="ChevronRight" size={16} />
            </button>
          </div>
        </div>
      </div>
      {/* ── Gantt Card ── */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_0_rgba(0,0,0,0.02)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3 text-[#9CA3AF]">
              <Icon name="Loader2" size={20} className="animate-spin text-primary" />
              <span className="text-sm text-[#6B7280]">{t('common.loading')}</span>
            </div>
          </div>
        ) : filteredProjects?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Icon name="Calendar" size={40} className="text-[#D1D5DB]" />
            <p className="text-[#9CA3AF] text-sm">{t('projectSchedule.empty')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {granularity === 'day' ? (
              /* ── DAY VIEW: fixed-width columns, two-row header ── */
              (<div style={{ minWidth: `${208 + columns?.length * DAY_COL_WIDTH}px` }}>
                {/* Header row 1: weekday letters */}
                <div className="flex border-b border-gray-100 bg-[#F9FAFB]">
                  <div className="flex-shrink-0 sticky left-0 bg-[#F9FAFB] z-10" style={{ width: '208px' }} />
                  {columns?.map((col, i) => (
                    <div
                      key={i}
                      className={`flex-shrink-0 flex items-center justify-center border-r border-gray-100 last:border-r-0 py-1 ${col?.isWeekend ? 'bg-gray-50' : ''}`}
                      style={{ width: `${DAY_COL_WIDTH}px` }}
                    >
                      <span className={`text-[10px] font-medium ${col?.isWeekend ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}`}>
                        {col?.weekday}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Header row 2: day number + month */}
                <div className="flex border-b border-gray-200 bg-[#F9FAFB]">
                  <div
                    className="flex-shrink-0 px-4 py-2 border-r border-gray-200 sticky left-0 bg-[#F9FAFB] z-10 flex items-center"
                    style={{ width: '208px' }}
                  >
                    <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Project</span>
                  </div>
                  {columns?.map((col, i) => (
                    <div
                      key={i}
                      className={`flex-shrink-0 flex items-center justify-center border-r border-gray-100 last:border-r-0 py-2 ${col?.isWeekend ? 'bg-gray-50' : ''}`}
                      style={{ width: `${DAY_COL_WIDTH}px` }}
                    >
                      <span className={`text-[10px] font-medium whitespace-nowrap ${col?.isWeekend ? 'text-[#9CA3AF]' : 'text-[#6B7280]'}`}>
                        {col?.label}
                      </span>
                    </div>
                  ))}
                </div>
                {/* Project rows */}
                <div className="relative">
                  {filteredProjects?.map((project) => {
                    const barStyle = getBarStyle(project);
                    const color = getPmColor(project?.pmUserId);
                    return (
                      <div
                        key={project?.id}
                        className="flex border-b border-gray-100 last:border-b-0 bg-white hover:border-primary transition-colors duration-150"
                        style={{ height: '52px' }}
                      >
                        {/* Sticky project name */}
                        <div
                          className="flex-shrink-0 px-4 flex flex-col justify-center border-r border-gray-200 sticky left-0 z-10 bg-white"
                          style={{ width: '208px' }}
                        >
                          <span className="text-xs font-semibold text-primary truncate">{project?.projectNumber}</span>
                          <span className="text-xs text-[#374151] truncate leading-tight mt-0.5">{project?.name}</span>
                        </div>
                        {/* Day columns + bar */}
                        <div className="relative flex" style={{ width: `${columns?.length * DAY_COL_WIDTH}px` }}>
                          {/* Column grid lines + weekend shading */}
                          {columns?.map((col, i) => (
                            <div
                              key={i}
                              className={`flex-shrink-0 border-r border-gray-100 last:border-r-0 h-full ${col?.isWeekend ? 'bg-gray-50/60' : ''}`}
                              style={{ width: `${DAY_COL_WIDTH}px` }}
                            />
                          ))}
                          {/* Today marker */}
                          {showTodayMarker && (
                            <div
                              className="absolute top-0 bottom-0 z-20 pointer-events-none"
                              style={{ left: `${todayPct}%` }}
                            >
                              <div className="w-px h-full border-l-2 border-dashed border-primary opacity-70" />
                            </div>
                          )}
                          {/* Project bar */}
                          {barStyle && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 rounded-md cursor-pointer flex items-center px-2 overflow-hidden transition-opacity hover:opacity-80 z-10"
                              style={{
                                ...barStyle,
                                height: '26px',
                                backgroundColor: color?.bg,
                              }}
                              onMouseEnter={(e) => setTooltip({ visible: true, project, x: e?.clientX, y: e?.clientY })}
                              onMouseMove={(e) => setTooltip(prev => ({ ...prev, x: e?.clientX, y: e?.clientY }))}
                              onMouseLeave={() => setTooltip({ visible: false, project: null, x: 0, y: 0 })}
                              onClick={() => navigate(`/project/${project?.id}/budget`)}
                            >
                              <span
                                className="text-xs font-semibold truncate select-none"
                                style={{ color: color?.text }}
                              >
                                {project?.projectNumber} {project?.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>)
            ) : (
              /* ── WEEK / MONTH / QUARTER VIEW (unchanged) ── */
              (<div style={{ minWidth: '900px' }}>
                {/* Header row */}
                <div className="flex border-b border-gray-200 bg-[#F9FAFB]">
                  <div className="flex-shrink-0 w-52 px-4 py-3 border-r border-gray-200 sticky left-0 bg-[#F9FAFB] z-10">
                    <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Project</span>
                  </div>
                  <div className="flex-1 relative flex">
                    {columns?.map((col, i) => (
                      <div
                        key={i}
                        className="flex-1 px-2 py-3 border-r border-gray-200 last:border-r-0 text-center"
                      >
                        <span className="text-xs font-medium text-[#6B7280] whitespace-nowrap">{col?.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                {/* Project rows */}
                <div className="relative">
                  {filteredProjects?.map((project) => {
                    const barStyle = getBarStyle(project);
                    const color = getPmColor(project?.pmUserId);
                    return (
                      <div
                        key={project?.id}
                        className="flex border-b border-gray-100 last:border-b-0 bg-white hover:border-primary transition-colors duration-150"
                        style={{ height: '52px' }}
                      >
                        <div className="flex-shrink-0 w-52 px-4 flex flex-col justify-center border-r border-gray-200 sticky left-0 z-10 bg-white">
                          <span className="text-xs font-semibold text-primary truncate">{project?.projectNumber}</span>
                          <span className="text-xs text-[#374151] truncate leading-tight mt-0.5">{project?.name}</span>
                        </div>
                        <div className="flex-1 relative">
                          {/* Column grid lines */}
                          <div className="absolute inset-0 flex pointer-events-none">
                            {columns?.map((_, i) => (
                              <div key={i} className="flex-1 border-r border-gray-100 last:border-r-0" />
                            ))}
                          </div>
                          {/* Today marker */}
                          {showTodayMarker && (
                            <div
                              className="absolute top-0 bottom-0 z-20 pointer-events-none"
                              style={{ left: `${todayPct}%` }}
                            >
                              <div className="w-px h-full border-l-2 border-dashed border-primary opacity-70" />
                            </div>
                          )}
                          {/* Project bar */}
                          {barStyle && (
                            <div
                              className="absolute top-1/2 -translate-y-1/2 rounded-md cursor-pointer flex items-center px-2 overflow-hidden transition-opacity hover:opacity-80 z-10"
                              style={{
                                ...barStyle,
                                height: '26px',
                                backgroundColor: color?.bg,
                              }}
                              onMouseEnter={(e) => setTooltip({ visible: true, project, x: e?.clientX, y: e?.clientY })}
                              onMouseMove={(e) => setTooltip(prev => ({ ...prev, x: e?.clientX, y: e?.clientY }))}
                              onMouseLeave={() => setTooltip({ visible: false, project: null, x: 0, y: 0 })}
                              onClick={() => navigate(`/project/${project?.id}/budget`)}
                            >
                              <span
                                className="text-xs font-semibold truncate select-none"
                                style={{ color: color?.text }}
                              >
                                {project?.projectNumber} {project?.name}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>)
            )}
          </div>
        )}
      </div>
      {/* Tooltip */}
      <Tooltip
        project={tooltip?.project}
        x={tooltip?.x}
        y={tooltip?.y}
        t={t}
        visible={tooltip?.visible}
      />
    </div>
  );
};

export default ProjectSchedulePage;
